import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress } from '@mui/material';
import { apiService } from '../services/apiService';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';

interface FilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  file: {
    name: string;
    type: string;
    url?: string;
    s3Uri?: string;
    size: number;
  } | null;
}

export default function FilePreviewModal({ open, onClose, file }: FilePreviewModalProps) {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open && file?.s3Uri) {
      loadFileContent();
    }
  }, [open, file]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFileContent = async () => {
    if (!file?.s3Uri) return;
    
    const fileType = file.type.toLowerCase();
    
    setLoading(true);
    setError('');
    setContent('');
    
    try {
      if (fileType === 'json') {
        // For JSON files, get text content
        const fileContent = await apiService.getFileContent(file.s3Uri);
        try {
          const jsonData = JSON.parse(fileContent);
          setContent(JSON.stringify(jsonData, null, 2));
        } catch {
          setContent(fileContent);
        }
      } else {
        // For other files, get signed URL for the document viewer
        const signedUrl = await apiService.getSignedDownloadUrl(file.s3Uri);
        setContent(signedUrl);
      }
    } catch (err) {
      console.error('File preview error:', err);
      setError('Preview not available for this file type');
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>File Preview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the Download button to open the file in its native application.
          </Typography>
        </Box>
      );
    }

    if (content && file) {
      const fileType = file.type.toLowerCase();
      

      
      // For JSON files, show as formatted text
      if (fileType === 'json') {
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>JSON Content</Typography>
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              border: '1px solid #ccc', 
              borderRadius: 1, 
              backgroundColor: '#f5f5f5', 
              maxHeight: 400, 
              overflow: 'auto' 
            }}>
              <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap' }}>
                {content}
              </pre>
            </Box>
          </Box>
        );
      }
      
      // For other file types, use DocViewer
      const docs = [{
        uri: content,
        fileName: file.name,
        fileType: file.type
      }];
      
      return (
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>Document Preview</Typography>
          <Box sx={{ 
            mt: 2, 
            border: '1px solid #ccc', 
            borderRadius: 1, 
            height: 400, 
            overflow: 'hidden' 
          }}>
            <DocViewer 
              documents={docs} 
              pluginRenderers={DocViewerRenderers}
              style={{ height: '100%' }}
              config={{
                header: {
                  disableHeader: false,
                  disableFileName: false,
                  retainURLParams: false
                }
              }}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>File Preview</Typography>
        <Typography variant="body2" color="text.secondary">
          No content available
        </Typography>
      </Box>
    );
  };

  if (!file) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {file.name}
        <Typography variant="caption" display="block" color="text.secondary">
          {file.type.toUpperCase()} • {(file.size / 1024).toFixed(1)} KB
        </Typography>
      </DialogTitle>
      <DialogContent>
        {renderPreview()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={async () => {
          try {
            if (file.url) {
              window.open(file.url, '_blank');
            } else if (file.s3Uri) {
              const signedUrl = await apiService.getSignedDownloadUrl(file.s3Uri, file.name);
              window.open(signedUrl, '_blank');
            }
          } catch (error) {
            console.error('Download failed:', error);
            alert('Download failed. Please try again.');
          }
        }}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}